import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import taxiLogo from "../bg2.jpg";
import api from "../utils/api"; // (for axios calls elsewhere)
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";


// -------------------------------
// ✅ CheckoutForm
// -------------------------------
const CheckoutForm = ({ booking }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isPaid, setIsPaid] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("🚀 handleSubmit triggered for booking:", booking);

    if (!stripe || !elements) return;

    // Prevent double payment
    if (sessionStorage.getItem(`paid_${booking.booking_id}`)) {
      setMessage("✅ This booking is already paid. Redirecting...");
      setTimeout(() => navigate(`/booking-confirmation/${booking.ref_code}`), 2000);
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setMessage("⚠️ Please enter valid card details.");
      return;
    }

    setLoading(true);
    setMessage("⌛ Initiating payment request...");

    try {
      const res = await fetch(`${API_BASE}/api/create-payment-intent`, {

        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bookingId: booking.booking_id,
          bookingRef: booking.ref_code,
          customerName: booking.customer_name,
          customerEmail: booking.email,
          pickup: booking.pickup_location,
          dropoff: booking.dropoff_location,
          amount: booking.fare
        }),
      });

      if (!res.ok) {
        setMessage(`❌ Server error: ${await res.text()}`);
        setLoading(false);
        return;
      }

      const { clientSecret } = await res.json();
      if (!clientSecret) {
        setMessage("❌ Failed to start payment. No client secret.");
        setLoading(false);
        return;
      }

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: cardElement },
      });

      if (result.error) {
        setMessage(`❌ ${result.error.message}`);
      } else if (result.paymentIntent.status === "succeeded") {
        setMessage("✅ Payment successful!");
        localStorage.setItem("userEmail", booking.email);

        setIsPaid(true);

	        await fetch(`${API_BASE}/api/payment-success`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ bookingId: booking.booking_id }),
        });

        sessionStorage.setItem(`paid_${booking.booking_id}`, "true");
        setTimeout(() => navigate(`/booking-confirmation/${booking.ref_code}`), 2000);
      }
    } catch (err) {
      console.error("🔥 Payment error:", err);
      setMessage("❌ Could not reach payment server.");
    }

    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <CardElement
        options={{
          style: {
            base: { fontSize: "16px", color: "#32325d", "::placeholder": { color: "#a0aec0" } },
          },
        }}
      />
      <button
        type="submit"
        disabled={!stripe || loading || isPaid}
        style={{
          backgroundColor: isPaid ? "#6c757d" : "#28a745",
          color: "white",
          padding: "12px 25px",
          fontSize: "16px",
          fontWeight: "bold",
          border: "none",
          borderRadius: "6px",
          cursor: isPaid ? "not-allowed" : "pointer",
          marginTop: "15px",
          width: "100%",
        }}
      >
        {loading ? "Processing..." : isPaid ? "Payment Completed ✅" : "💳 Pay Now"}
      </button>
      {message && (
        <p style={{ marginTop: "10px", color: message.includes("✅") ? "green" : "red" }}>
          {message}
        </p>
      )}
    </form>
  );
};

// -------------------------------
// ✅ Payment Page
// -------------------------------
const Payment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    if (!token) {
      setError("⚠️ Invalid payment link.");
      setLoading(false);
      return;
    }

    const verifyTokenAndFetch = async () => {
      try {
        // 1️⃣ Verify token
        const verifyRes = await fetch(`${API_BASE}/api/verify-payment-token`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });

        const verifyData = await verifyRes.json();

        if (!verifyData.valid) {
          setError("⚠️ Payment link is invalid or has expired.");
          setLoading(false);
          return;
        }

        // 2️⃣ Save email for later
        if (verifyData.email) {
          localStorage.setItem("userEmail", verifyData.email);
        }

        // 3️⃣ Fetch booking securely
const response = await fetch(`${API_BASE}/api/bookings/${verifyData.bookingId}`, {

          headers: { "x-user-email": verifyData.email || "" }
        });

        if (response.status === 403) {
          setError("⚠️ Unauthorized to access this booking.");
          setLoading(false);
          return;
        }

        const data = await response.json();

        if (data.has_paid) {
          navigate(`/booking-confirmation/${data.ref_code}`);
          return;
        }

        setBooking(data);
      } catch (err) {
        console.error("Error verifying token or fetching booking:", err);
        setError("⚠️ Unable to load payment details.");
      } finally {
        setLoading(false);
      }
    };

    verifyTokenAndFetch();
  }, [location.search, navigate]);

  if (loading) {
    return <div style={{ textAlign: "center", marginTop: "50px" }}><h2>Loading payment details...</h2></div>;
  }

  if (error) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <h2>{error}</h2>
        <button onClick={() => navigate("/")} style={{ padding: "10px 20px", backgroundColor: "#007bff", color: "#fff", border: "none", borderRadius: "6px" }}>Return Home</button>
      </div>
    );
  }

  if (!booking) {
    return <div style={{ textAlign: "center", marginTop: "50px" }}><h2>Booking not found.</h2></div>;
  }

  return (
    <div style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100vh",
      backgroundColor: "#f8f9fa"
    }}>
      <div style={{
        background: "#fff",
        padding: "25px 30px",
        borderRadius: "12px",
        textAlign: "center",
        maxWidth: "380px",
        width: "100%",
        boxShadow: "0 6px 14px rgba(0,0,0,0.12)"
      }}>
        <h2 style={{ marginBottom: "18px", color: "#333" }}>Payment Page</h2>
        <p><strong>Booking Reference:</strong> {booking.ref_code}</p>
        <p><strong>Pickup:</strong> {booking.pickup_location}</p>
        <p><strong>Dropoff:</strong> {booking.dropoff_location}</p>
        <p><strong>Fare:</strong> £{booking.fare}</p>

        <CheckoutForm booking={booking} />

        <img
          src={taxiLogo}
          alt="Taxi Service"
          style={{ maxWidth: "220px", marginTop: "20px", objectFit: "contain" }}
        />
      </div>
    </div>
  );
};

export default Payment;

